import { useEffect, useMemo, useRef, useState, useId } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import dayjs, { type Dayjs } from 'dayjs';
import { Clock, CircleAlert } from 'lucide-react';
import { cn } from './cn';

export interface TimePickerProps {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  label?: React.ReactNode;
  error?: string | boolean;
  helperText?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Minute granularity of the popover list. Default 15. */
  step?: number;
  placeholder?: string;
  fullWidth?: boolean;
  className?: string;
}

// Time is carried on a fixed anchor date so consumers can format('HH:mm').
const ANCHOR = '2000-01-01';

/** Mask free typing into "HH:MM" shape (digits + optional colon, max 2+2). */
const maskTime = (raw: string): string => {
  const cleaned = raw.replace(/[^\d:]/g, '');
  const colonIdx = cleaned.indexOf(':');
  if (colonIdx >= 0) {
    const hh = cleaned.slice(0, colonIdx).replace(/:/g, '').slice(0, 2);
    const mm = cleaned.slice(colonIdx + 1).replace(/:/g, '').slice(0, 2);
    return mm.length > 0 || cleaned.includes(':') ? `${hh}:${mm}` : hh;
  }
  const digits = cleaned.slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

/** Strict parse of a fully typed "HH:MM" (validates 00-23 / 00-59). */
const parseStrict = (text: string): string | null => {
  const m = /^(\d{2}):(\d{2})$/.exec(text);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${m[1]}:${m[2]}`;
};

/** Forgiving parse used on commit (blur/Enter): "8" → 08:00, "8:5" → 08:05. */
const parseLoose = (text: string): string | null => {
  const m = /^(\d{1,2})(?::(\d{1,2}))?$/.exec(text);
  if (!m) return null;
  const h = Number(m[1]);
  const min = m[2] !== undefined ? Number(m[2].padStart(2, '0')) : 0;
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

const toDayjs = (hhmm: string): Dayjs => dayjs(`${ANCHOR}T${hhmm}`);

/** Minutes-of-day for nearest-option highlighting/scrolling. */
const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/**
 * 24-hour time field — fully custom branded control (no native time input).
 * Text input with HH:MM masking + popover list of times (15-min steps by default),
 * following the DatePicker popover pattern. value/onChange are dayjs objects
 * carried on a fixed anchor date so consumers can format('HH:mm').
 */
export function TimePicker({
  value,
  onChange,
  label,
  error,
  helperText,
  required,
  disabled,
  step = 15,
  placeholder = 'HH:MM',
  fullWidth = true,
  className,
}: TimePickerProps) {
  const inputId = useId();
  const listboxId = useId();
  const hasError = Boolean(error);
  const errorText = typeof error === 'string' ? error : undefined;

  const valueStr = value && value.isValid() ? value.format('HH:mm') : '';

  const [open, setOpen] = useState(false);
  const [text, setText] = useState(valueStr);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the draft text in sync with external value changes.
  useEffect(() => {
    setText(valueStr);
  }, [valueStr]);

  // Outside click / Escape closes the popover (same pattern as DatePicker).
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const options = useMemo(() => {
    const safeStep = Math.max(1, Math.floor(step) || 15);
    const list: string[] = [];
    for (let m = 0; m < 24 * 60; m += safeStep) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      list.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
    }
    return list;
  }, [step]);

  // Item to scroll into view on open: exact value, else the nearest option (08:00 default).
  const activeOption = useMemo(() => {
    const target = valueStr ? toMinutes(valueStr) : toMinutes('08:00');
    let best = options[0];
    let bestDist = Infinity;
    for (const opt of options) {
      const dist = Math.abs(toMinutes(opt) - target);
      if (dist < bestDist) {
        bestDist = dist;
        best = opt;
      }
    }
    return best;
  }, [options, valueStr]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: 'center' });
  }, [open]);

  const commitText = (raw: string) => {
    if (raw.trim() === '') {
      setText('');
      if (valueStr) onChange(null);
      return;
    }
    const parsed = parseLoose(raw.trim());
    if (parsed) {
      setText(parsed);
      if (parsed !== valueStr) onChange(toDayjs(parsed));
    } else {
      // Invalid entry — revert to the last committed value.
      setText(valueStr);
    }
  };

  const handleInput = (raw: string) => {
    const masked = maskTime(raw);
    setText(masked);
    if (masked === '') {
      if (valueStr) onChange(null);
      return;
    }
    // Fully typed valid time commits live so onChange fires as the user finishes.
    const strict = parseStrict(masked);
    if (strict && strict !== valueStr) onChange(toDayjs(strict));
  };

  const selectOption = (opt: string) => {
    setText(opt);
    if (opt !== valueStr) onChange(toDayjs(opt));
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={cn('relative flex flex-col gap-1.5', fullWidth && 'w-full', className)}
    >
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-content">
          {label}
          {required && <span className="ml-0.5 text-primary">*</span>}
        </label>
      )}

      <div
        className={cn(
          'flex h-11 items-center gap-2 rounded-xl border bg-surface px-3.5 transition-all duration-200',
          'focus-within:ring-2 focus-within:ring-primary/30',
          // Field-validation errors use the danger (red) token + icon (CONVENTIONS §5).
          hasError
            ? 'border-danger ring-2 ring-danger/25 focus-within:border-danger focus-within:ring-danger/40'
            : 'border-line hover:border-primary/40 focus-within:border-primary',
          disabled && 'opacity-60 pointer-events-none',
        )}
        onMouseDown={(e) => {
          // Clicking anywhere in the field focuses the input and opens the popover.
          if (e.target !== inputRef.current) {
            e.preventDefault();
            inputRef.current?.focus();
            setOpen(true);
          }
        }}
      >
        <Clock size={18} className="shrink-0 text-muted" />
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={text}
          placeholder={placeholder}
          maxLength={5}
          required={required}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-haspopup="listbox"
          aria-invalid={hasError || undefined}
          onFocus={() => setOpen(true)}
          onChange={(e) => handleInput(e.target.value)}
          onBlur={(e) => commitText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commitText((e.target as HTMLInputElement).value);
              setOpen(false);
            } else if (e.key === 'ArrowDown' && !open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
          className="min-w-0 flex-1 border-0 bg-transparent text-sm tabular-nums text-content outline-none placeholder:text-muted/70"
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute left-0 top-full z-[1400] mt-2 w-full min-w-[7.5rem] rounded-2xl border border-line bg-surface p-1.5 text-content shadow-pop"
          >
            <div
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={typeof label === 'string' ? label : undefined}
              className="flex max-h-56 flex-col gap-0.5 overflow-y-auto overscroll-contain pr-0.5"
            >
              {options.map((opt) => {
                const selected = opt === valueStr;
                return (
                  <button
                    key={opt}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    data-active={opt === activeOption || undefined}
                    // preventDefault keeps focus in the input so blur-commit
                    // doesn't race the click selection.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectOption(opt)}
                    className={cn(
                      'shrink-0 rounded-lg px-3 py-1.5 text-left text-sm tabular-nums transition-colors duration-100',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                      selected
                        ? 'bg-primary font-semibold text-white'
                        : 'text-content hover:bg-primary/10',
                    )}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {errorText ? (
        <p className="flex items-start gap-1.5 text-xs font-medium text-danger">
          <CircleAlert size={13} strokeWidth={2.5} className="mt-px shrink-0" aria-hidden="true" />
          <span>{errorText}</span>
        </p>
      ) : helperText ? (
        <p className="text-xs text-muted">{helperText}</p>
      ) : null}
    </div>
  );
}
